import Action from "csi/def/Action";
import { Field } from "csi/def/Field";
import Relationship from "csi/def/Relationship";
import { View } from "csi/def/View";
import ViewRef from "csi/def/ViewRef";
import Widget from "csi/form/Widget";
import locale from "csi/locale";
import csi from "csi/main";
import resolver from "csi/resolver";
import { cnl, ICache } from "csi/typings";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";

const APPLICABLE_FOR_COLORS = "ApplicableForColors";
const COLORWAY = "Colorway";
const COLOR_MATERIAL = "ColorMaterial";
const FROM_BOM = "FromBOM";
const FROM_BOM_CARD = "ApparelBOM.TableView";
const FROM_CARE_AND_COMPOSITION = "FromCareAndComposition";
const MATERIAL = "Material";
const NODE_NAME = "Node Name";
const PRODUCT_COLORS = "ProductColors";
const SP_TEMPLATE = "SPTemplate";
const SP_TEMPLATE_CARD = "SustainabilityProfile.Template";
const STYLE = "Style";
const SUBTYPE = "Subtype";

const SustainabilityProfileForm = declare(Widget, {

	/** @property {Field} _applicableForColorsField - Field for ApplicableForColors */
	_applicableForColorsField: null,

	/** @property {Field} _spTemplateField - Field for SPTemplate */
	_spTemplateField: null,

	/** @property {Field} _fromBOMField - Field for FromBOM */
	_fromBOMField: null,

	/** @property {Field} _fromCareAndCompositionField - Field for FromCareAndComposition */
	_fromCareAndCompositionField: null,

	/** @property {Field} _nodeNameField - Field for Node Name */
	_nodeNameField: null,

	/** @property {Field} _subtypeField - Field for subtype */
	_subtypeField: null,

	/** @property {ICache} cache - Cache for querying SPTemplates/revisions */
	cache: null,

	/** @property {boolean} initialForm - True if form is first initialized */	
	initialForm: true,

	/** @property {C8Node} bomRev - The selected BOM's latest revision */	
	bomRev: null,

	/** @property {C8Node} selectedBOM - FromBOM that is selected */	
	selectedBOM : null,

	/** @property {C8Node} spRev - The SustainabilityProfile's latest revision (used in Edit case) */	
	spRev: null,

	/**
	 * @override
	 * Manage field disabling/enabling
	 */
	_resolveView(): void {
		this.inherited(arguments);

		this.cache = this.getCache() as ICache;
		this._applicableForColorsField = this.getField(APPLICABLE_FOR_COLORS);
		this._spTemplateField = this.getField(SP_TEMPLATE);
		this._fromBOMField = this.getField(FROM_BOM);
		this._fromCareAndCompositionField = this.getField(FROM_CARE_AND_COMPOSITION);
		this._nodeNameField = this.getField(NODE_NAME);

		this._subtypeField = this.getField(SUBTYPE);
		if (this._subtypeField != null) {
			this.setFieldDisabled(this._subtypeField, true);
		}

		this._spTemplateField.dialogCardId = SP_TEMPLATE_CARD;
		this._spTemplateField.m_onChange = "_onChangeSPTemplate";
		this._fromBOMField.dialogCardId = FROM_BOM_CARD;
		this._fromBOMField.m_onChange = "_onChangeFromBOM";
		if (this.isNew()) {
			this.setFieldValue(this._applicableForColorsField, []);
		}

		if (this.isCopy()) {
			this.setFieldDisabled(this._spTemplateField, true);
		}

		this._applicableForColorsField.widgetParams =  {
			defaultAll:"true", 
		};
	},

	/**
	 * @override
	 * Get field store for ApplicableForColors
	 */
	getFieldStore(field: Field, value: String) {
		if (field.getControlName() === APPLICABLE_FOR_COLORS) {
			const ctx = this.getContext();
			const product = this.cache.getNode(this.getFormNode().__Parent__);

			var nodeType = "";
			switch (product.$Type) {
				case STYLE:
					nodeType = COLORWAY;
					break;
				case MATERIAL:
					nodeType = COLOR_MATERIAL;
					break;
				default:
					break;
			}

			var rel;
			if (this.selectedBOM != null && this.bomRev.BOMProductColors.length > 0) {
				rel = lang.mixin(new Relationship(), {
					m_typeNode: nodeType,
					m_query: new Query()
						.Node(xml.PROP_URL, xml.OP_EQUALS, this.selectedBOM.LatestRevision, "Parent:BOMProductColors"),
				});
			} else {
				rel = lang.mixin(new Relationship(), {
					m_typeNode: nodeType,
					m_query: new Query()
						.RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, product.$URL),	
				});
			}

			var store = ctx.view.createHeadingNamesStore(ctx, field, value, rel);

			// create view for Go to Dialog
			if (this.selectedBOM != null && this.bomRev.BOMProductColors.length > 0) {
				store.action = lang.mixin(new Action(), {
					m_viewRef: lang.mixin(new ViewRef(), {
						m_id: PRODUCT_COLORS,
						m_url: this.selectedBOM.LatestRevision,
					}),
				});
			} else {
				store.action = lang.mixin(new Action(), {
					m_viewRef: lang.mixin(new ViewRef(), {
						m_id: PRODUCT_COLORS,
						m_url: product.$URL,
					}),
				});
			}

			return store;
		} else {
			return this.inherited(arguments);
		}
	},

	_onChangeSPTemplate(defView: View, field: Field, value: cnl): void {
		this.setFieldValue(this._fromBOMField, csi.EMPTY_URL);
		this.resetFieldStoreAndValue(this._applicableForColorsField, []);
		if (value !== csi.EMPTY_URL) {
			this.selectedBOM = null;
			this.bomRev = null;
			this.setFieldHidden(this._applicableForColorsField, false);
			this._processSPTemplateSelected(value);
		} else {
			this._setFieldsHidden(true);
			this.setFieldValue(this._subtypeField, csi.EMPTY_URL);
		}
	},

	_processSPTemplateSelected(value: cnl): void {
		const selectedSPTemplate = this.cache.getNode(value);
		if (this._subtypeField != null) {
			this.setFieldValue(this._subtypeField, selectedSPTemplate.Subtype);
		}

		let hasBOMRefs = false;
		let hasCareAndCompRefs = false;

		const spRev = resolver.resolveNode(selectedSPTemplate.CurrentRevision, true, this.cache);
		const propertiesCustomView = resolver.resolveNode(spRev.PropertiesCustomView, true, this.cache);

		if (propertiesCustomView != null) {
			const keyList = Object.keys(propertiesCustomView.Keys).map((key) => propertiesCustomView.Keys[key]);
			hasBOMRefs = !!keyList.find((key) => key.includes("Child:FromBOM") || key === "FromBOM:(SustainabilityProfileRevision):0");
			hasCareAndCompRefs = !!keyList.find((key) => key.includes("Child:FromCareAndComposition") || key === "FromCareAndComposition:(SustainabilityProfileRevision):0");
		}

		if (!hasBOMRefs) {
			this.setFieldHidden(this._fromBOMField, true);
			if (this.isNew()) {
				this.setFieldValue(this._applicableForColorsField, []);
			}
		} else {
			this.setFieldHidden(this._fromBOMField, false);
			const selectedBOM = this.getFieldValue(this._fromBOMField);
			this.setFieldValue(this._fromBOMField, csi.EMPTY_URL);
			this.setFieldValue(this._fromBOMField, selectedBOM);
		}

		const productType = this.cache.getNode(this.getFormNode().__Parent__).$Type;
		this.setFieldHidden(this._fromCareAndCompositionField, !hasCareAndCompRefs || productType !== STYLE);

		if ((this.isEdit() || this.isCopy()) && this.initialForm && this.spRev) {
			this.setFieldValue(this._fromCareAndCompositionField, this.spRev.FromCareAndComposition);
			this.setFieldValue(this._fromBOMField, this.spRev.FromBOM);
			this.setFieldValue(this._applicableForColorsField, this.spRev.ApplicableForColors);
		} else {
			// Automatically preset value if there is only 1 BOM or C&C
			hasBOMRefs && this._presetValueForAggregateStoreField(this._fromBOMField);
			hasCareAndCompRefs && productType === STYLE && this._presetValueForAggregateStoreField(this._fromCareAndCompositionField);
		}

		// Delete SP template custom view node from cache to always fetch most updated custom view to avoid full page refresh
		this.cache.deleteNode(spRev.PropertiesCustomView);
	},

	/**
	 * Presets the aggregate store if there is only 1 value that can be selected
	 * @param field
	 */
	_presetValueForAggregateStoreField(field: Field) {
		this.resetFieldStoreAndValue(field, csi.EMPTY_URL);
		const storeData = field.aggregateStoreResponse?.groups?.at(0)?.results;
		if (storeData?.length === 1) {
			this.setFieldValue(field, storeData.at(0).$id);
		};
	},

	_onChangeFromBOM(defView: View, field: Field, value: cnl): void {
		if (this.initialForm && (this.isCopy() || this.isEdit())) {
			this.initialForm = false;
			return;
		} else if (!this.isFieldVisible(this._fromBOMField) || value == csi.EMPTY_URL) {
			this.selectedBOM = null;
			this.bomRev = null;
			this.setFieldValue(this._applicableForColorsField, []);
			
			// reset field store to update store to query for all ProductColors under the product
			this.resetFieldStoreAndValue(this._applicableForColorsField, []);
		} else {
			this.selectedBOM = resolver.resolveNode(value, true, this.cache);
			this.bomRev = resolver.resolveNode(this.selectedBOM.LatestRevision, true, this.cache);
			
			/* Reset field store to update store to query for BOMProductColors on the selected BOM, 
			 * preselecting all entries by passing in empty list which will default to "All" */
			if (this.bomRev.BOMProductColors.length > 0) {
				this.resetFieldStoreAndValue(this._applicableForColorsField, []);
			} else {
				// preselect all colors on the product if selected BOM has no colors
				const product = this.cache.getNode(this.getFormNode().__Parent__);
				this.resetFieldStoreAndValue(this._applicableForColorsField, []);
			}
		}
	},

	/**
	 * @override
	 * Hide BOM, C&C, ApplicableForColors fields if it is a New Sustainability Profile form
	 * @param fields
	 */
	_buildFields(fields: Field[]): void {
		this.inherited(arguments);

		this._setFieldsHidden(true);

		if (this.isCopy()) {
			this.setFieldValue(this._nodeNameField, locale.resource("NAME_COPY", [this.getFieldValue(this._nodeNameField)]));
		}

		// Reset spTemplate field values on copy and edit so that the form is updated
		// to the correct state (runs the logic to show/hide BOM/C&C)
		if (this.isCopy() || this.isEdit()) {
			const spTemplate = this.getFieldValue(this._spTemplateField);
			this.setFieldValue(this._spTemplateField, csi.EMPTY_URL);
			this.setFieldValue(this._spTemplateField, spTemplate);

			this.spRev = this.cache.getNode(this.getViewNode().CurrentRevision);
		}
	},

	/**
	 * Hide/unhide ApplicableForColors, FromBOM, FromCareAndComposition fields
	 */
	_setFieldsHidden(hidden: boolean): void {
		this.setFieldHidden(this._applicableForColorsField, hidden);
		this.setFieldHidden(this._fromBOMField, hidden);
		this.setFieldHidden(this._fromCareAndCompositionField, hidden);
	},
});

export = SustainabilityProfileForm;
